import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RefreshIcon from "@mui/icons-material/Refresh";
import {deferredActionsRun, getAllDeferredActions} from "../api/AiAgentApi";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

export const DeferredActionPage = () => {
    const navigate = useNavigate();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingSend, setLoadingSend] = useState(false);
    const [err, setErr] = useState(null);

    const handleCreateScript = () => navigate("/create-script");
    const handleCreateDeferredAction = () => navigate("/create-deferred-action");

    const fetchData = async () => {
        try {
            setLoading(true);
            setErr(null);
            const data = await getAllDeferredActions(); // [{id,name,isEnabled,createdAt}, ...]
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            setErr(e?.message || "Не удалось загрузить данные");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const rows = useMemo(
        () =>
            items
                ?.slice()
                .sort((a, b) => (b.createdAt ?? "").localeCompare?.(a.createdAt ?? "")),
        [items]
    );

    const fmt = (iso) => {
        try {
            const d = new Date(iso);
            return isNaN(d.getTime()) ? String(iso ?? "") : d.toLocaleString("ru-RU");
        } catch {
            return String(iso ?? "");
        }
    };

    const handleRun = async () => {
        setLoadingSend(true);
        try {
            await deferredActionsRun();
            alert("✅ Действие успешно выполнено");
        } catch (err) {
            console.error(err);
            alert("❌ Не удалось выполнить действие");
        } finally {
            setLoadingSend(false);
        }
    };

    return (
        <Box p={3}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                <Typography variant="h5" fontWeight="bold">
                    Отложенные действия
                </Typography>

                <Stack direction="row" spacing={2}>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={!loadingSend && <PlayArrowIcon />}
                        onClick={handleRun}
                        disabled={loadingSend}
                        sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            px: 3,
                            py: 1,
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                        }}
                    >
                        {loadingSend ? <CircularProgress size={22} color="inherit" /> : "Запустить"}
                    </Button>

                    <Button
                        variant="outlined"
                        startIcon={<ScheduleIcon />}
                        onClick={handleCreateDeferredAction}
                        sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            px: 3,
                            py: 1.2,
                            boxShadow: 1,
                            "&:hover": { boxShadow: 3 }
                        }}
                    >
                        Создать отложенное действие
                    </Button>

                    <Tooltip title="Обновить список">
                        <IconButton onClick={fetchData} sx={{ ml: 1 }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {loading && (
                <Stack alignItems="center" py={6}>
                    <CircularProgress />
                    <Typography variant="body2" mt={2} color="text.secondary">
                        Загружаем отложенные действия…
                    </Typography>
                </Stack>
            )}

            {!loading && err && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {err}
                </Alert>
            )}

            {!loading && !err && rows?.length === 0 && (
                <Paper sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Пока нет отложенных действий
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                        Нажмите «Создать отложенное действие», чтобы добавить первое.
                    </Typography>
                </Paper>
            )}

            {!loading && !err && rows?.length > 0 && (
                <TableContainer component={Paper}>
                    <Table size="medium" aria-label="deferred-actions">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 110 }}>ID</TableCell>
                                <TableCell>Название</TableCell>
                                <TableCell sx={{ width: 160 }}>Статус</TableCell>
                                <TableCell sx={{ width: 220 }}>Создано</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.id} hover onClick={() => {
                                    navigate('/deferred-actions/details?id=' + r.id);
                                }} style={{ cursor: "pointer" }}>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {r.id}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="subtitle2">{r.name}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={r.isEnabled ? "Включено" : "Выключено"}
                                            color={r.isEnabled ? "success" : "default"}
                                            variant={r.isEnabled ? "filled" : "outlined"}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{fmt(r.createdAt)}</Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};
